import { AudioLines, MessageSquare, ChevronRight, X, Mic, MicOff } from 'lucide-react';

export interface AgentDockProps {
  agentStatus: 'disconnected' | 'connecting' | 'authenticating' | 'connected' | 'ending' | 'error';
  isUserMuted: boolean;
  isPushToTalkLocked: boolean;
  sessionDuration: string;
  isAgentActive: boolean;
  isAgentTyping: boolean;
  isUserSpeaking?: boolean;
  isAgentSpeaking?: boolean;
  onConnectAgent: (mode?: string) => void;
  onPushToTalkStart: () => void;
  onPushToTalkEnd: () => void;
  onToggleLock: () => void;
  onStopConversation: () => void;
  floating?: boolean;
  column1Width?: number;
  hasCanvasContent?: boolean;
  onSendMessage?: (message: string) => void;
  chatInputText?: string;
  onChatInputTextChange?: (text: string) => void;
  showChatInput?: boolean;
  hasMessages?: boolean;
  onShowChatInput?: () => void;
  onHideChatInput?: () => void;
  isChatColumnVisible?: boolean;
  onToggleChatColumn?: () => void;
  showColumnToggles?: boolean;
  isNotebookColumnVisible?: boolean;
  onToggleNotebookColumn?: () => void;
}

const AgentDock = ({
  agentStatus,
  isUserMuted,
  isPushToTalkLocked,
  isAgentActive,
  isUserSpeaking = false,
  isAgentSpeaking = false,
  onConnectAgent,
  onPushToTalkStart,
  onPushToTalkEnd,
  onToggleLock,
  onStopConversation,
  floating = false,
  onSendMessage,
  chatInputText,
  onChatInputTextChange,
  showChatInput = false,
  onHideChatInput,
  isChatColumnVisible = false,
  onToggleChatColumn,
  showColumnToggles = false,
}: AgentDockProps) => {

  // Determine current speaking state for status text
  const getStatusContent = () => {
    if (agentStatus === 'connecting' || agentStatus === 'authenticating') {
      return {
        text: 'Connecting...',
        dotColor: 'bg-amber-400',
        pillClass: 'bg-white/80',
        showMic: false,
      };
    }

    if (agentStatus === 'ending') {
      return {
        text: 'Ending...',
        dotColor: 'bg-gray-400',
        pillClass: 'bg-white/80',
        showMic: false,
      };
    }

    if (agentStatus === 'connected') {
      if (isUserSpeaking) {
        return {
          text: "You're speaking...",
          dotColor: 'bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.6)]',
          pillClass: 'bg-gradient-to-r from-cyan-50/90 to-white/90 border-cyan-200/60',
          showMic: true,
        };
      }
      if (isAgentSpeaking) {
        return {
          text: 'StudiBudi speaking...',
          dotColor: 'bg-gradient-to-r from-pink-400 to-cyan-400 shadow-[0_0_8px_rgba(236,72,153,0.5)]',
          pillClass: 'bg-gradient-to-r from-pink-50/80 via-white/90 to-cyan-50/80',
          showMic: true,
        };
      }
      // Idle/listening state
      return {
        text: isPushToTalkLocked ? 'Listening...' : 'Hold to talk',
        dotColor: 'bg-emerald-400',
        pillClass: 'bg-white/85',
        showMic: true,
      };
    }

    return null;
  };

  const statusContent = getStatusContent();

  // Premium floating styles with glassmorphism
  const wrapperClasses = floating
    ? 'fixed bottom-6 left-1/2 -translate-x-1/2 z-50'
    : 'relative';

  return (
    <div className={wrapperClasses}>
      {/* Collapsible Chat Input */}
      {agentStatus === 'disconnected' && showChatInput && (
        <div className="mb-3 flex items-start transition-all duration-300 ease-in-out">
          <button onClick={onHideChatInput} className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0 mr-1">
            <X className="w-4 h-4 text-gray-500" />
          </button>
          <div className="flex flex-col gap-0.5 bg-white/95 backdrop-blur-xl border border-white/60 rounded-2xl p-1.5 flex-1 min-w-[280px] shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
            <textarea
              value={chatInputText ?? ''}
              onChange={e => onChatInputTextChange?.(e.target.value)}
              placeholder="Type something..."
              className="w-full p-2 bg-transparent border-0 focus:outline-none text-gray-900 placeholder-gray-500 text-[13px] resize-none overflow-y-auto"
              rows={2}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  const msg = (chatInputText ?? '').trim();
                  if (msg && onSendMessage) {
                    onSendMessage(msg);
                    onChatInputTextChange?.('');
                  }
                }
              }}
            />
            <button
              onClick={() => {
                const msg = (chatInputText ?? '').trim();
                if (msg && onSendMessage) {
                  onSendMessage(msg);
                  onChatInputTextChange?.('');
                }
              }}
              disabled={!(chatInputText ?? '').trim()}
              className={`self-end p-2 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${(chatInputText ?? '').trim() ? 'bg-black text-white' : 'bg-white text-black border border-gray-300'}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="19" x2="12" y2="5"></line>
                <polyline points="5 12 12 5 19 12"></polyline>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Main Dock Container - Glassmorphism pill */}
      <div
        className="flex items-center gap-2 px-2 py-1.5 rounded-full backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.08),0_2px_8px_rgba(0,0,0,0.04)] border border-white/40"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.75) 100%)',
        }}
      >
        {/* ===== DISCONNECTED STATE ===== */}
        {agentStatus === 'disconnected' && !isAgentActive && (
          <>
            <button
              data-tour="talk-button"
              className="h-10 px-5 rounded-full bg-gradient-to-r from-cyan-400/20 to-blue-400/20 hover:from-cyan-400/30 hover:to-blue-400/30 transition-all duration-300 flex items-center gap-2 whitespace-nowrap"
              onClick={() => onConnectAgent('general')}
            >
              <AudioLines className="w-4 h-4 text-cyan-600 flex-shrink-0" />
              <span className="text-slate-700 font-medium text-sm">
                Talk to Studibudi
              </span>
            </button>

            {showColumnToggles && (
              <button
                onClick={onToggleChatColumn}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-all ${isChatColumnVisible
                  ? 'bg-blue-100 text-blue-600'
                  : 'hover:bg-gray-100 text-gray-500'
                  }`}
                title={isChatColumnVisible ? 'Hide transcript' : 'Show transcript'}
              >
                {isChatColumnVisible ? <ChevronRight className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
              </button>
            )}
          </>
        )}

        {/* ===== CONNECTED / CONNECTING STATES ===== */}
        {(agentStatus === 'connecting' || agentStatus === 'authenticating' || agentStatus === 'connected' || agentStatus === 'ending') && statusContent && (
          <>
            {/* Push-to-talk pill */}
            <div
              className={`h-10 px-4 rounded-full ${statusContent.pillClass} flex items-center gap-2.5 transition-all duration-200 whitespace-nowrap ${agentStatus === 'connected' && !isPushToTalkLocked
                  ? 'cursor-pointer hover:shadow-md active:scale-[0.98]'
                  : ''
                } ${!isUserMuted ? 'ring-2 ring-cyan-300/40 ring-offset-1 ring-offset-transparent' : ''}`}
              onMouseDown={agentStatus === 'connected' && !isPushToTalkLocked ? onPushToTalkStart : undefined}
              onMouseUp={agentStatus === 'connected' && !isPushToTalkLocked ? onPushToTalkEnd : undefined}
              onMouseLeave={agentStatus === 'connected' && !isPushToTalkLocked && !isUserMuted ? onPushToTalkEnd : undefined}
              onTouchStart={agentStatus === 'connected' && !isPushToTalkLocked ? onPushToTalkStart : undefined}
              onTouchEnd={agentStatus === 'connected' && !isPushToTalkLocked ? onPushToTalkEnd : undefined}
            >
              {/* Status dot with glow */}
              <div className={`w-2 h-2 rounded-full ${statusContent.dotColor} ${agentStatus === 'connecting' ? 'animate-pulse' : ''}`} />

              {/* Status text */}
              <span className="text-slate-600 text-sm font-medium select-none">
                {statusContent.text}
              </span>

              {/* Lock/PTT toggle */}
              {statusContent.showMic && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleLock();
                  }}
                  className={`w-7 h-7 flex items-center justify-center rounded-full transition-all ${isPushToTalkLocked
                      ? (!isUserMuted ? 'bg-cyan-100 text-cyan-600' : 'bg-gray-100 text-gray-400')
                      : 'bg-amber-100 text-amber-600'
                    }`}
                  title={isPushToTalkLocked ? 'Switch to push-to-talk' : 'Lock mic on'}
                >
                  {!isUserMuted ? <Mic className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5" />}
                </button>
              )}
            </div>

            {/* End/Cancel Button */}
            <button
              onClick={onStopConversation}
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-all ${agentStatus === 'connected'
                  ? 'bg-red-50 hover:bg-red-100 text-red-500'
                  : 'hover:bg-gray-100 text-gray-500'
                }`}
              title={agentStatus === 'connected' ? 'End call' : 'Cancel'}
            >
              <X className="w-4 h-4" />
            </button>

            {/* Transcript Toggle */}
            {showColumnToggles && (
              <button
                onClick={onToggleChatColumn}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-all ${isChatColumnVisible
                  ? 'bg-blue-100 text-blue-600'
                  : 'hover:bg-gray-100 text-gray-500'
                  }`}
                title={isChatColumnVisible ? 'Hide transcript' : 'Show transcript'}
              >
                {isChatColumnVisible ? <ChevronRight className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AgentDock;